import React, { useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer, withNulls } from "../hrForm";
import { useMasterOptions } from "../hrForm";
import EmployeeSelect from "../EmployeeSelect";
import { EmptyState, Field, LoadState, PrimaryButton, SecondaryButton } from "../hrComponents";
import AppDropdown from "../../../components/AppDropdown/AppDropdown";
import { cardStyle, formatDate, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../hrUi";

// HOL-01..03: holiday calendars per year. One is the default for everyone; a location or one person can have their own.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const thisYear = new Date().getFullYear();
const WEEKDAYS = [["1", "Mon"], ["2", "Tue"], ["3", "Wed"], ["4", "Thu"], ["5", "Fri"], ["6", "Sat"], ["7", "Sun"]];

const weeklyOffLabel = (v) =>
  String(v || "")
    .split(",")
    .filter(Boolean)
    .map((n) => (WEEKDAYS.find(([k]) => k === n) || [])[1])
    .join(", ");

function CalendarDrawer({ show, onHide, onDone }) {
  const templates = useLoad(() => hrmsService.listHolidayTemplates(), [], { enabled: show });
  const settings = useLoad(() => hrmsService.getSettings(), [], { enabled: show });
  // Only offer templates for the tenant's own country (AE or IN) - never both.
  const country = settings.data?.country_pack;
  const list = (templates.data || []).filter((t) => !country || t.country_pack === country);
  return (
    <FormDrawer
      show={show}
      title="New holiday calendar"
      initial={{ year: thisYear, template: "BLANK" }}
      onHide={onHide}
      onDone={onDone}
      onSave={(clean) => (clean.template && clean.template !== "BLANK" ? hrmsService.calendarFromTemplate({ ...clean, year: Number(clean.year) }) : hrmsService.saveCalendar(null, { name: clean.name, year: Number(clean.year) }))}
      fields={[
        { key: "year", label: "Year", type: "number", required: true, min: 2000 },
        { key: "template", label: "Start from", type: "select", options: [{ label: "Blank calendar", value: "BLANK" }, ...list.map((t) => ({ label: t.label, value: t.key }))], hint: "A template fills in the public holidays for the year. Moon-sighting dates are added as tentative." },
        { key: "name", label: "Name", max: 100, hint: "Leave blank to use the template's name." },
      ]}
    />
  );
}

function HolidayDrawer({ calendarId, holiday, onHide, onDone }) {
  return (
    <FormDrawer
      show={!!calendarId}
      title={holiday ? "Edit holiday" : "Add holiday"}
      initial={holiday ? { ...holiday } : { holiday_type: "PUBLIC", is_confirmed: true }}
      onHide={onHide}
      onDone={onDone}
      onSave={(clean, raw) => (holiday ? hrmsService.updateHoliday(holiday.id, withNulls(raw, ["holiday_date", "name", "holiday_type", "is_confirmed"])) : hrmsService.addHoliday(calendarId, clean))}
      fields={[
        { key: "holiday_date", label: "Date", type: "date", required: true },
        { key: "name", label: "Name", required: true, max: 120 },
        { key: "holiday_type", label: "Type", type: "select", options: [{ label: "Public holiday", value: "PUBLIC" }, { label: "Optional holiday", value: "OPTIONAL" }, { label: "Tentative", value: "TENTATIVE" }] },
        { key: "is_confirmed", label: "Date confirmed", type: "checkbox" },
      ]}
    />
  );
}

function AssignDrawer({ calendar, onHide, onDone }) {
  const [type, setType] = useState("LOCATION");
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const options = useMasterOptions(["locations"]);
  React.useEffect(() => {
    if (calendar) {
      setType("LOCATION");
      setTarget(null);
    }
  }, [calendar]);
  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.assignCalendar(calendar.id, { target_type: type, target_id: type === "ALL" ? undefined : target });
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={!!calendar}
      onHide={() => !saving && onHide()}
      title="Who follows this calendar"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || (type !== "ALL" && !target)}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <Field label="Applies to">
        <AppDropdown
          value={type}
          options={[{ label: "Everyone (default)", value: "ALL" }, { label: "A location", value: "LOCATION" }, { label: "One employee", value: "EMPLOYEE" }]}
          onChange={(e) => { setType(e.value); setTarget(null); }}
        />
      </Field>
      {type === "LOCATION" ? (
        <Field label="Location" required>
          <AppDropdown value={target} options={options.locations || []} onChange={(e) => setTarget(e.value)} placeholder="Select location" />
        </Field>
      ) : null}
      {type === "EMPLOYEE" ? (
        <Field label="Employee" required>
          <EmployeeSelect value={target} onChange={setTarget} />
        </Field>
      ) : null}
      <div style={{ fontSize: 12, color: "#6B7280" }}>The most specific wins: a person’s own calendar, then their location’s, then the default.</div>
    </SlidePanel>
  );
}

function CalendarDetail({ id, onBack, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getCalendar(id), [id]);
  const [holiday, setHoliday] = useState(undefined); // undefined closed, null add, object edit
  const [assigning, setAssigning] = useState(null);
  const [copying, setCopying] = useState(false);
  const changed = () => {
    reload();
    onChanged();
  };
  const remove = async (h) => {
    const res = await hrmsService.removeHoliday(h.id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) changed();
    else toast.error(hrmsService.responseMessage(res));
  };
  const unassign = async (a) => {
    const res = await hrmsService.unassignCalendar(id, a.id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) changed();
    else toast.error(hrmsService.responseMessage(res));
  };

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {data ? (
        <>
          <button type="button" className="btn btn-link px-0 mb-2" onClick={onBack}>
            ← All calendars
          </button>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div>
              <h5 className="mb-0">
                {data.name} · {data.year}
              </h5>
              <div style={{ fontSize: 13, color: "#6B7280" }}>Weekly off: {weeklyOffLabel(data.weekly_off_days) || "—"}</div>
            </div>
            <div className="d-flex gap-2">
              <SecondaryButton onClick={() => setCopying(true)}>Copy to another year</SecondaryButton>
              <SecondaryButton onClick={() => setAssigning(data)}>Who follows it</SecondaryButton>
              <PrimaryButton onClick={() => setHoliday(null)}>Add holiday</PrimaryButton>
            </div>
          </div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            Applies to: {(data.assignments || []).length ? data.assignments.map((a) => (
              <span key={a.id} style={{ ...statusBadge("PROBATION"), marginRight: 6 }}>
                {a.target_name}{" "}
                <button type="button" aria-label={`Remove ${a.target_name}`} onClick={() => unassign(a)} style={{ background: "none", border: "none", padding: 0, color: "inherit" }}>
                  ×
                </button>
              </span>
            )) : <span style={{ color: "#92400E" }}>nobody yet</span>}
          </div>
          {data.holidays.length === 0 ? (
            <EmptyState title="No holidays in this calendar" />
          ) : (
            <div style={{ ...cardStyle, overflowX: "auto" }}>
              <table className="table mb-0" style={{ minWidth: 520 }}>
                <thead>
                  <tr>
                    {["Date", "Holiday", "Type", ""].map((h) => (
                      <th key={h} style={thStyle}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.holidays.map((h) => (
                    <tr key={h.id}>
                      <td style={tdStyle}>{formatDate(h.holiday_date)}</td>
                      <td style={tdStyle}>{h.name}</td>
                      <td style={tdStyle}>
                        {statusLabel(h.holiday_type)}
                        {!h.is_confirmed ? <span style={{ color: "#92400E", fontSize: 12 }}> · to be confirmed</span> : null}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button type="button" style={linkBtn} onClick={() => setHoliday(h)}>
                          Edit
                        </button>{" "}
                        <button type="button" style={linkBtn} onClick={() => remove(h)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <HolidayDrawer calendarId={holiday === undefined ? null : id} holiday={holiday || null} onHide={() => setHoliday(undefined)} onDone={changed} />
          <AssignDrawer calendar={assigning} onHide={() => setAssigning(null)} onDone={changed} />
          <FormDrawer
            show={copying}
            title="Copy calendar"
            initial={{ year: data.year + 1 }}
            onHide={() => setCopying(false)}
            onSave={(clean) => hrmsService.copyCalendar(id, { year: Number(clean.year) })}
            onDone={onChanged}
            fields={[{ key: "year", label: "Copy into year", type: "number", required: true, hint: "Fixed-date holidays move to the new year. Moon-sighting ones need their dates entered again." }]}
          />
        </>
      ) : null}
    </LoadState>
  );
}

export default function HolidaysTab() {
  const [year, setYear] = useState(thisYear);
  const { data, loading, error, reload } = useLoad(() => hrmsService.listCalendars({ year }), [year]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  if (openId) return <CalendarDetail id={openId} onBack={() => setOpenId(null)} onChanged={reload} />;
  const rows = data || [];
  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setYear(year - 1)} aria-label="Previous year">
          ‹
        </button>
        <strong>{year}</strong>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setYear(year + 1)} aria-label="Next year">
          ›
        </button>
        <PrimaryButton onClick={() => setCreating(true)}>New calendar</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title={`No holiday calendar for ${year}`} text="Create one from your country’s template, then choose who follows it." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["Calendar", "Holidays", "Weekly off", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td style={tdStyle}>{c.name}</td>
                    <td style={tdStyle}>{c.holiday_count}</td>
                    <td style={tdStyle}>{weeklyOffLabel(c.weekly_off_days)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => setOpenId(c.id)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <CalendarDrawer show={creating} onHide={() => setCreating(false)} onDone={(d) => { reload(); if (d?.id) setOpenId(d.id); }} />
    </>
  );
}
